import ky from "ky"
import { atom } from "jotai"
import cookies from "js-cookie"
import { isPast } from "date-fns"
import { unwrap, loadable } from "jotai/utils"

import { debug } from "~/lib/logger.client"

import type { SessionResponse } from "~/api/session"

const d = debug.extend("atoms:session")

/**
 * If the user has an access token in their local storage, set
 * the legacy-customer-access-token cookie, so that the session
 * endpoint will keep the customer logged in, and return a fresh
 * HttpOnly cookie.
 *
 * The @dotdev packages use the "store" npm package to store local
 * storage values, which means quiet a bit of faffing to decode them
 * without installing the legacy tools, hence the defensive nature
 * of this function.
 *
 * @todo Remove when most legacy tokens have reasonably been migrated
 * to HttpOnly cookies. If a customer has not been migrated at removal
 * they will need to login again.
 */
function setLegacyAccessTokenCookie() {
  try {
    // Get the raw local storage item.
    const item = localStorage.getItem("headless:customer")
    if (!item) return

    // It is stored as JSON encoded string, which itself is a JSON encoded object.
    const jsonString = JSON.parse(item)
    if (!jsonString || "string" !== typeof jsonString) return

    // The, we must decode the JSON encoded object.
    const json = JSON.parse(jsonString)
    if (!json || "object" !== typeof json) return

    // Extract the access token.
    const accessToken = (json as any).value?.accessToken as unknown
    if (!accessToken || "string" !== typeof accessToken) return

    // Check if the access token is expired.
    const expiresAt = (json as any).value?.expiresAt as unknown
    if (expiresAt && "string" === expiresAt && isPast(expiresAt)) return

    // Set session cookie, removed when browser closed.
    d("Resolved legacy customer access token, setting cookie.", { accessToken })
    cookies.set("legacy-customer-access-token", accessToken)
  } catch (e) {
    // Do nothing if token parsing fails.
    d("Error thrown in setLegacyAccessTokenCookie", e)
  }
}

/**
 * Response from /api/session endpoint.
 */
export const sessionAtom = atom(async () => {
  setLegacyAccessTokenCookie()

  try {
    d("Sending request to /api/session.")
    const res = await ky.get("/api/session").json<SessionResponse>()
    d("Received response from /api/session", { res })
    return res
  } catch (e) {
    d("Request to /api/session failed.", { e })
    console.error(`Request to /api/session failed.`, e)
    return { customer: null, sessionId: "failed" } satisfies SessionResponse
  }
})

/**
 * Response from /api/session endpoint (unwrapped).
 */
export const sessionUnwrappedAtom = unwrap(sessionAtom, prev => prev ?? null)

/**
 * Response from /api/session endpoint (loadable).
 */
export const sessionLoadableAtom = loadable(sessionAtom)

/**
 * True if the session has resolved.
 */
export const sessionResolvedAtom = atom(get => {
  return !!get(sessionUnwrappedAtom)
})

/**
 * Customer from session.
 */
export const sessionCustomerAtom = atom(get => {
  const session = get(sessionUnwrappedAtom)
  return session?.customer ?? null
})

/**
 * Session ID from session.
 */
export const sessionIdAtom = atom(get => {
  const session = get(sessionUnwrappedAtom)
  return session?.sessionId ?? null
})
